import { useEffect, useRef } from "react";

type Variant = "up" | "left" | "right" | "zoom";

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  variant?: Variant;
  delay?: number; // ms
  as?: keyof JSX.IntrinsicElements;
}

const variantClass: Record<Variant, string> = {
  up: "reveal-up",
  left: "reveal-left",
  right: "reveal-right",
  zoom: "reveal-zoom",
};

const Reveal = ({ children, className = "", variant = "up", delay = 0, as: Tag = "div" }: RevealProps) => {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Respect reduced motion and skip observer entirely
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      el.classList.add("revealed");
      return;
    }
    // Already in view on mount: reveal at once, with no delay, as before.
    // The check reads the observer's first report rather than calling
    // getBoundingClientRect here: with a dozen Reveals on a page, each one
    // reading layout right after the previous one added a class forced the
    // browser to lay the page out again and again during the first render.
    let first = true;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (first) {
          first = false;
          const r = entry.boundingClientRect;
          const viewH = entry.rootBounds?.height ?? window.innerHeight;
          const inView = r ? r.top < viewH && r.bottom > 0 : entry.isIntersecting;
          if (inView) {
            el.classList.add("revealed");
            obs.disconnect();
            return;
          }
        }
        if (entry.isIntersecting) {
          if (delay) setTimeout(() => el.classList.add("revealed"), delay);
          else el.classList.add("revealed");
          obs.disconnect();
        }
      },
      { threshold: 0.05, rootMargin: "0px 0px -10% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);

  const Component = Tag as React.ElementType;
  return (
    <Component ref={ref} className={`${variantClass[variant]} ${className}`}>
      {children}
    </Component>
  );
};

export default Reveal;
